import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import GridToolbarNoExport from "../../../common/GridToolbarNoExport";
import AppDataGrid from "../../../common/AppDataGrid";
import useFetchGradeReport from "../hooks/GradeReportHook";

const GradeReport = ({ schoolId }) => {
  const { data, loading } = useFetchGradeReport(schoolId);
  const [searchText, setSearchText] = useState("");

  const columns = [
    { field: "District", headerName: "District", flex: 1 },
    { field: "High_School", headerName: "High School", flex: 1 },
    { field: "Tartan_Id", headerName: "Tartan ID", flex: 1 },
    { field: "SSID", headerName: "SSID", flex: 1 },
    { field: "First_Name", headerName: "First Name", flex: 1 },
    { field: "Last_Name", headerName: "Last Name", flex: 1 },
    { field: "Graduation_Year", headerName: "Graduation Year", flex: 1 },
    { field: "Course", headerName: "Course", flex: 1 },
    { field: "Course_Title", headerName: "Course Title", flex: 1 },
    { field: "Course_Start_Date", headerName: "Course Start Date", flex: 1 },
    { field: "Course_End_Date", headerName: "Course End Date", flex: 1 },
    { field: "Restricted", headerName: "Restricted", flex: 1 },
    { field: "Credit_Hour_Count", headerName: "Credit Hour Count", flex: 1 },
    { field: "Cumulative_GPA", headerName: "Cumulative GPA", flex: 1 },
    { field: "Term_GPA", headerName: "Term GPA", flex: 1 },
    { field: "Credit_Hrs_Earned", headerName: "Credit Hours Earned", flex: 1 },
  ];

  // Filter rows based on search text
  const filteredRows = data.filter((row) => {
    if (!searchText) return true;

    const searchLower = searchText.toLowerCase();
    return Object.values(row).some((value) =>
      value?.toString().toLowerCase().includes(searchLower),
    );
  });

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  // Export every field present on each row — not just the columns shown
  // in the grid — since the underlying report rows can carry more fields
  // than this view chooses to display. Columns that ARE shown in the grid
  // come first, in the same order/labels as the on-screen report; any
  // extra fields the grid doesn't display are appended after, under their
  // raw field name.
  const handleExportExcel = () => {
    if (filteredRows.length === 0) return;

    const gridFields = columns.map((col) => col.field);
    const extraFields = Array.from(
      new Set(filteredRows.flatMap((row) => Object.keys(row))),
    ).filter((field) => field !== "id" && !gridFields.includes(field));

    const dataForSheet = filteredRows.map((row) => {
      const rowData = {};
      columns.forEach((col) => {
        rowData[col.headerName || col.field] = row[col.field] ?? "";
      });
      extraFields.forEach((field) => {
        rowData[field] = row[field] ?? "";
      });
      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataForSheet);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(
      workbook,
      `Grade_Report_${dayjs().format("YYYY-MM-DD")}.xlsx`,
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Card>
      <CardHeader
        title="Grade Report"
        action={
          <Tooltip title="Download to Excel">
            <IconButton
              onClick={handleExportExcel}
              sx={{ color: "white" }}
              aria-label="Download to Excel"
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        }
        sx={{ backgroundColor: "#AC1A2F", color: "white" }}
      />
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search grades..."
            value={searchText}
            onChange={handleSearchChange}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#AC1A2F",
                },
                "&:hover fieldset": {
                  borderColor: "#AC1A2F",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#AC1A2F",
                },
              },
            }}
          />
        </Box>
        <AppDataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.id}
          slots={{ toolbar: GridToolbarNoExport }}
          autosizeOnMount
          autoHeight
          pagination
          initialState={{
            ...data.initialState,
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10, 25, 50, { value: -1, label: "All" }]}
        />
      </CardContent>
    </Card>
  );
};

export default GradeReport;
